import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Color3, Color4, Plane, Quaternion, Vector3, Vector4 } from '@babylonjs/core/Maths';
import { PlaneBuilder } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { getBinClampedSize } from '@common-util/texture-util';
import { AnnotationNode } from '@models/classes/annotations';
import { eAnnotationLineStyle } from '@models/classes/enums';
import { Connector } from '@models/classes/features/connector';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { SceneNode } from '@models/classes/scene-node';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces';
import { getCameraPositionWithOffset, rotationToVector, roundToPlaces, transformVectorWithQuat } from '@view/helpers';
import { Subscription } from 'rxjs';
import { KbViewer } from '../kb-viewer';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';

const TEXT_RESOLUTION = 128;
export interface ILineMeshOptions {
    points: Vector3[];
    updatable?: boolean;
    instance?: LinesMesh;
    colors?: Color4[];
    dashSize?: number;
    gapSize?: number;
    dashNb?: number;
}
export interface AnnotationParts {
    line?: ILineMeshOptions;
    textMesh: AbstractMesh;
    textMaterial: PBRMaterial;
    textContainer: TransformNode;
    rootMesh: TransformNode;
    billboard: boolean;
}

export interface AnnotationInfo {
    meshInfo?: AnnotationParts;
    boundingInfo?: {
        targetNode: Kb3dObject;
        subscription: Subscription;
    };
    positionNeedsUpdate: boolean;
    textNeedsUpdate: boolean;
    lineNeedsUpdate: boolean;
}

export class AnnotationRenderer extends Renderer<AnnotationNode, TransformNode> {
    utilityLayer: UtilityLayerRenderer;
    modelToken = Token.AnnotationNode;
    annotationStore = new Map<string, AnnotationInfo>();
    billboards = new Map<string, AnnotationParts>();

    constructor(protected viewer: KbViewer, protected scene: Scene, protected rendererManager: RendererManager) {
        super(viewer, scene);
        this.utilityLayer = viewer.utilityLayer;
        scene.registerBeforeRender(() => {
            if (scene.activeCamera && this.billboards.size > 0) {
                this.billboards.forEach(meshInfo => {
                    const camera = scene.activeCamera!;
                    meshInfo.rootMesh.lookAt(getCameraPositionWithOffset(camera), 0, Math.PI / 2);
                    if (meshInfo.rootMesh.parent) {
                        if (!meshInfo.rootMesh.rotationQuaternion) {
                            meshInfo.rootMesh.rotationQuaternion = Quaternion.Identity();
                        }
                        const parentRotation = meshInfo.rootMesh.parent.computeWorldMatrix().getRotationMatrix();
                        parentRotation.invert();
                        const parentRotationQuat = Quaternion.FromRotationMatrix(parentRotation);
                        meshInfo.rootMesh.rotationQuaternion = parentRotationQuat.multiplyInPlace(
                            meshInfo.rootMesh.rotationQuaternion
                        );
                    }
                });
            }
        });
    }

    update3dInternal(node: ITrackedClass<AnnotationNode>) {
        let annotationInfo = this.annotationStore.get(node._dynamicId);
        if (!annotationInfo) {
            annotationInfo = {
                positionNeedsUpdate: false,
                textNeedsUpdate: false,
                lineNeedsUpdate: false,
            };
            this.annotationStore.set(node._dynamicId, annotationInfo);
        }

        if (annotationInfo.meshInfo && node.changes.has('keepOnTop')) {
            this.delete3d(node, true);
            annotationInfo.meshInfo = undefined;
            annotationInfo.positionNeedsUpdate = true;
            annotationInfo.textNeedsUpdate = true;
            annotationInfo.lineNeedsUpdate = true;
        }
        const scene = node.keepOnTop ? this.utilityLayer.utilityLayerScene : this.scene;

        if (
            (!annotationInfo.meshInfo && node.enabled && node.targetId) ||
            (node.changes.hasAny('isPickable') && node.enabled && node.targetId)
        ) {
            if (annotationInfo.meshInfo) {
                annotationInfo.meshInfo = undefined;
            }
            annotationInfo.meshInfo = createAnnotationTextModels(node, scene);

            // Put the annotation on the parent scene so the scene visibility affects the node
            if (node._parentScene) {
                const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
                annotationInfo.meshInfo.rootMesh.setParent(parentSceneMesh);
            }

            this.set3d(node, annotationInfo.meshInfo.rootMesh);
        }
        const meshInfo = annotationInfo.meshInfo;

        // Set position of annotation
        if (node.changes.hasAny('targetId', 'offset', 'billboard', 'horizontalBoxAlign', 'verticalBoxAlign')) {
            annotationInfo.positionNeedsUpdate = true;
        }
        if (node.changes.hasAny('color', 'lineStyle')) {
            annotationInfo.lineNeedsUpdate = true;
        }
        if (
            node.changes.hasAny(
                'fontFamily',
                'fontSize',
                'lineSpacing',
                'label',
                'color',
                'preloadFontStylesheet',
                'horizontalTextAlign'
            )
        ) {
            annotationInfo.textNeedsUpdate = true;
        }

        if (meshInfo) {
            if (node.changes.hasAny('enabled', 'billboard', 'keepOnTop')) {
                if (node.enabled && node.billboard) {
                    this.billboards.set(node._dynamicId, meshInfo);
                } else {
                    this.billboards.delete(node._dynamicId);
                }
            }

            let needsFont = false;
            if (node.preloadFontStylesheet && node.fontFamily) {
                const fontService = node._manager!.getService(Token.FontService);

                if (!fontService.readyFonts.has(node.fontFamily)) {
                    needsFont = true;
                    fontService.loadFont(node.preloadFontStylesheet, [node.fontFamily]).then(resultFonts => {
                        if (resultFonts.length > 0) {
                            node.setPropertyIsModified('fontFamily', true);
                        }
                    });
                }
            }

            if (node.enabled && node.targetId && !needsFont) {
                const color = node.color.toColor4();

                if (annotationInfo.positionNeedsUpdate) {
                    let targetNode: Kb3dObject | undefined;
                    if (node.targetId && node._manager) {
                        const manager = node._manager;
                        targetNode = manager.getById(node.targetId);
                    }

                    if (
                        annotationInfo.boundingInfo &&
                        annotationInfo.boundingInfo.targetNode._dynamicId !== targetNode?._dynamicId
                    ) {
                        annotationInfo.boundingInfo.subscription.unsubscribe();
                    }
                    if (targetNode) {
                        if (targetNode instanceof Connector) {
                            // Only supporting connectors for now
                            if (targetNode._parent) {
                                annotationInfo.boundingInfo = {
                                    targetNode: targetNode,
                                    subscription: this.viewer.bbCache
                                        .watchForChanges(targetNode._parent)
                                        .subscribe(() => {
                                            node.setPropertyIsModified('targetId', true);
                                        }),
                                };
                            }
                        }

                        const transform = this.updatePositionFromAnnotation(node, targetNode, meshInfo);

                        if (transform) {
                            meshInfo.rootMesh.position = transform.position;
                            // The annotation plane mesh position doesn't update in certain situations when the parent transform is moved,
                            // so we have to manually update the world matrix
                            meshInfo.rootMesh.getChildMeshes().forEach(mesh => mesh.computeWorldMatrix(true));
                            meshInfo.textContainer.rotationQuaternion = transform.textRotation;
                            if (!node.billboard) {
                                meshInfo.rootMesh.rotationQuaternion = transform.rotation;
                            }

                            if (transform && transform.offset && transform.lineOffset && node.offset.length() > 0) {
                                const basePoint = transform.position.subtract(transform.offset);
                                meshInfo.line = {
                                    points: [basePoint, basePoint.add(transform.lineOffset)],
                                    updatable: true,
                                    colors: [color, color],
                                    instance: meshInfo.line?.instance,
                                };

                                // If the line is not getting rebuilt, then just update the line position
                                if (!annotationInfo.lineNeedsUpdate) {
                                    meshInfo.line.instance = drawLine(node, meshInfo.line, 1, scene);
                                }
                            } else {
                                if (meshInfo.line?.instance) {
                                    meshInfo.line.instance.dispose();
                                }
                                meshInfo.line = undefined;
                            }
                        }

                        annotationInfo.positionNeedsUpdate = false;
                    }
                }

                if (annotationInfo.lineNeedsUpdate) {
                    if (meshInfo.line) {
                        if (meshInfo.line.instance) {
                            meshInfo.line.instance.dispose();
                            meshInfo.line.instance = undefined;
                        }

                        meshInfo.line.colors = [];
                        for (let i = 0; i < meshInfo.line.points.length; i++) {
                            meshInfo.line.colors.push(color);
                        }
                        meshInfo.line.instance = drawLine(node, meshInfo.line, 1, scene);
                        if (meshInfo.line.instance && node._parentScene) {
                            const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
                            meshInfo.line.instance.parent = parentSceneMesh;
                        }

                        annotationInfo.lineNeedsUpdate = false;
                    }
                }

                if (annotationInfo.textNeedsUpdate) {
                    updateAnnotationText(
                        node,
                        meshInfo.textMesh,
                        meshInfo.textMaterial,
                        this.scene.getEngine().webGLVersion < 2
                    );
                    annotationInfo.textNeedsUpdate = false;
                }
            }

            const enabled = node.enabled && !!node.targetId;

            if (meshInfo.rootMesh.isEnabled() !== enabled) {
                meshInfo.rootMesh.setEnabled(enabled);
                meshInfo.line?.instance?.setEnabled(enabled);
            }
        }
    }

    dispose() {
        this.billboards.clear();
        this.annotationStore.clear();
        super.dispose();
    }

    delete3d(node: ITrackedClass<AnnotationNode>, keepInStore = false) {
        const annotationModels = this.annotationStore.get(node._dynamicId);
        if (annotationModels && annotationModels.meshInfo) {
            annotationModels.meshInfo.rootMesh.dispose();
            if (annotationModels.meshInfo.line?.instance) {
                annotationModels.meshInfo.line.instance.dispose();
            }
        }
        if (!keepInStore) {
            this.annotationStore.delete(node._dynamicId);
            this.billboards.delete(node._dynamicId);
        }
        super.delete3d(node);
    }

    private updatePositionFromAnnotation(
        node: ITrackedClass<AnnotationNode>,
        targetNode: Kb3dObject,
        meshInfo?: AnnotationParts
    ) {
        let newPosition: Vector3 | undefined;
        let newDirection: Vector3 | undefined;
        let newAngle: number | undefined;

        // Only supporting connectors for now, but could support direct mesh targets in the future
        if (targetNode instanceof Connector) {
            newPosition = getPositionFromConnnector(targetNode, this.rendererManager, node._parentScene);
            newDirection = targetNode.direction.toVec3();
            newAngle = targetNode.angle;
        }
        if (newPosition && newDirection && newAngle !== undefined) {
            const newOffset = node.offset.toVec3();
            if (meshInfo) {
                const newPivot = new Vector3(0, 0, 0);
                if (node.horizontalBoxAlign == 'left') {
                    meshInfo.rootMesh.getPivotPoint();
                    newOffset.x += meshInfo.textMesh.scaling.x / 2;
                    newPivot.x -= meshInfo.textMesh.scaling.x / 2;
                } else if (node.horizontalBoxAlign == 'right') {
                    newOffset.x -= meshInfo.textMesh.scaling.x / 2;
                    newPivot.x += meshInfo.textMesh.scaling.x / 2;
                }
                if (node.verticalBoxAlign == 'top') {
                    newOffset.z += meshInfo.textMesh.scaling.z / 2;
                    newPivot.z -= meshInfo.textMesh.scaling.z / 2;
                } else if (node.verticalBoxAlign == 'bottom') {
                    newOffset.z -= meshInfo.textMesh.scaling.z / 2;
                    newPivot.z += meshInfo.textMesh.scaling.z / 2;
                }
                meshInfo.rootMesh.setPivotPoint(newPivot);
            }

            const transform = calculateTransform(newPosition, newDirection, newAngle, newOffset, node.offset.toVec3());
            return transform;
        }
    }
}

export function createAnnotationTextModels(node: AnnotationNode, scene: Scene, isPickable = false) {
    const containerMesh = new TransformNode(node._dynamicId, scene);

    //Create plane
    const plane = PlaneBuilder.CreatePlane(
        `${node._dynamicId}_plane`,
        {
            width: 1,
            height: 1,
            frontUVs: new Vector4(1, 0, 0, 1),
            backUVs: new Vector4(0, 0, 1, 1),
            sideOrientation: Mesh.DOUBLESIDE,
            sourcePlane: new Plane(0, 1, 0, 0),
        },
        scene
    );
    const planeContainer = new TransformNode(`${node._dynamicId}_planeContainer`);

    const dynamicTexture = new DynamicTexture(
        `${node._dynamicId}_planeTexture`,
        { width: TEXT_RESOLUTION, height: TEXT_RESOLUTION },
        scene,
        true
    );
    dynamicTexture.isBlocking = false;
    dynamicTexture.hasAlpha = true;

    //create material
    const mat = new PBRMaterial(`${node._dynamicId}_mat`, scene);
    if (node.keepOnTop) {
        mat.emissiveColor = Color3.White();
    }
    mat.albedoTexture = dynamicTexture;
    mat.useAlphaFromAlbedoTexture = true;
    mat.backFaceCulling = true;
    mat.zOffset = -1;
    mat.albedoColor = Color3.White();
    mat.roughness = 1;
    mat.metallic = 0;

    //apply material
    plane.material = mat;
    plane.parent = planeContainer;
    planeContainer.parent = containerMesh;
    plane.isPickable = isPickable || node.isPickable;

    const annotationModels = {
        textMesh: plane,
        textContainer: planeContainer,
        textMaterial: mat,
        rootMesh: containerMesh,
        billboard: node.billboard,
        positionNeedsUpdate: false,
        textNeedsUpdate: false,
    };

    return annotationModels;
}

export function updateAnnotationText(
    node: AnnotationNode,
    mesh: AbstractMesh,
    textMaterial: PBRMaterial,
    webgl1Context: boolean,
    defaultValue = ' ',
    values: any = {}
) {
    const mat = textMaterial;
    const dynamicTexture = mat.albedoTexture as DynamicTexture;
    const fontFamily = node.fontFamily || 'Arial';
    const labelSize = node.fontSize;
    const labelText = evalHandlebarString(node.label || defaultValue, values);
    const textGap = node.lineSpacing;
    const textColor = node.color.toColor3().toLinearSpace();
    if (node.keepOnTop) {
        mat.emissiveColor = textColor;
    }

    // Determine the space needed for the text
    const ctx = dynamicTexture.getContext();
    ctx.font = TEXT_RESOLUTION + 'px ' + fontFamily;
    const labelStats = getMultiLineStats(labelText, ctx as CanvasRenderingContext2D);
    const textWidth = labelStats.maximumWidth;
    const textHeight = labelStats.maximumHeight;
    const labelArray = labelStats.labelArray;
    const lineWidths = labelStats.lineWidths;
    labelArray.reverse();
    lineWidths.reverse();
    const totalCanvasHeight = (textHeight + textGap) * labelArray.length - textGap;
    ctx.clearRect(0 - textWidth / 2, 0, textWidth / 2, totalCanvasHeight);
    //ctx.clearRect(0, 0, textWidth, totalCanvasHeight);
    let heightRatioChange = 1;
    if (labelArray.length > 1) {
        heightRatioChange = totalCanvasHeight / textHeight;
    }
    mesh.scaling = new Vector3((textWidth / textHeight) * labelSize, labelSize * heightRatioChange, 1);

    let textBaseline: CanvasTextBaseline = 'bottom';
    if (labelStats.maximumDescent > 0) {
        textBaseline = 'alphabetic';
    }
    let drawFont: string;
    if (webgl1Context) {
        let textureSize = getBinClampedSize(textWidth, totalCanvasHeight, 'ceil');
        if (textureSize > 2048) {
            textureSize = 2048;
        }
        dynamicTexture.scaleTo(textureSize, textureSize);
        ctx.scale(textureSize / textWidth, textureSize / totalCanvasHeight);
        drawFont = TEXT_RESOLUTION + 'px ' + fontFamily;
    } else {
        dynamicTexture.scaleTo(textWidth, totalCanvasHeight);
        drawFont = TEXT_RESOLUTION + 'px ' + fontFamily;
    }
    ctx.font = drawFont;
    if (ctx instanceof CanvasRenderingContext2D) {
        ctx.textBaseline = textBaseline;
    }
    ctx.fillStyle = textColor.toHexString();

    let drawHeight = totalCanvasHeight;
    for (let i = 0; i < labelArray.length; i++) {
        const lineWidth = lineWidths[i];
        //ctx.fillStyle = 'rgb(132,244,235, 0.3)';
        //ctx.fillRect(0, 0, textWidth, (textHeight + textGap) * labelArray.length - textGap);
        //rightAllign
        if (node.horizontalTextAlign === 'right') {
            ctx.fillText(labelArray[i], textWidth - lineWidth, drawHeight - labelStats.maximumDescent);
        }
        //leftAllign
        else if (node.horizontalTextAlign === 'left') {
            ctx.fillText(labelArray[i], 0, drawHeight - labelStats.maximumDescent);
        } else {
            //centered:
            ctx.fillText(labelArray[i], textWidth / 2 - lineWidth / 2, drawHeight - labelStats.maximumDescent);
        }
        drawHeight = drawHeight - textHeight - textGap;
    }
    // reset the context
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    dynamicTexture.update(true);
}

export function getPositionFromConnnector(
    targetNode: Connector,
    rendererManager: RendererManager,
    relativeToScene: SceneNode | undefined
) {
    const targetMesh = rendererManager.connector.get3d(targetNode);
    if (targetMesh) {
        targetMesh.computeWorldMatrix(true);
        const result = targetMesh.absolutePosition.clone();

        if (relativeToScene) {
            const sceneMesh = rendererManager.viewer.getRendererNode(relativeToScene);
            if (sceneMesh) {
                // console.log('pre', newPosition, newDirection, newAngle);
                const transform = sceneMesh.getWorldMatrix().clone();
                transform.invert();
                return Vector3.TransformCoordinates(result, transform);
            }
        }
        return result;
    }
    return Vector3.Zero();
}

export function calculateTransform(
    position: Vector3,
    direction: Vector3,
    angle: number,
    offset?: Vector3,
    lineOffset?: Vector3
) {
    const newPosition = position.clone();
    const newRotation = rotationToVector(direction);
    const textRotation = Quaternion.RotationAxis(Vector3.Up(), (angle - 90) * (Math.PI / 180));
    let offsetWithTransform: Vector3 | undefined;
    let lineOffsetWithTransform: Vector3 | undefined;

    if (offset) {
        offsetWithTransform = transformVectorWithQuat(offset, newRotation);
        newPosition.addInPlace(offsetWithTransform);
    }

    if (lineOffset) {
        lineOffsetWithTransform = transformVectorWithQuat(lineOffset, newRotation);
    }

    return {
        position: newPosition,
        rotation: newRotation,
        textRotation: textRotation,
        offset: offsetWithTransform,
        lineOffset: lineOffsetWithTransform,
    };
}

export function drawLine(
    node: AnnotationNode,
    lineInfo: AnnotationParts['line'],
    lineLength: number,
    scene: Scene,
    distance?: number
) {
    if (lineInfo && node.lineStyle !== eAnnotationLineStyle.none) {
        let linesMesh: LinesMesh;
        if (node.lineStyle === eAnnotationLineStyle.solid) {
            linesMesh = MeshBuilder.CreateLines(`${node._dynamicId}_line`, lineInfo, scene);
        } else {
            lineInfo.dashNb = lineLength * 30;
            if (node.lineStyle === eAnnotationLineStyle.dashed) {
                lineInfo.gapSize = 4;
                lineInfo.dashSize = 6;
                if (distance) {
                    // lineInfo.gapSize *= distance;
                    // lineInfo.dashSize *= distance;
                    lineInfo.dashNb = (lineInfo.dashNb / distance) * 1.5;
                }
            } else {
                lineInfo.gapSize = 6;
                lineInfo.dashSize = 1;
            }
            linesMesh = MeshBuilder.CreateDashedLines(`${node._dynamicId}_line`, lineInfo, scene);
            linesMesh.color = node.color.toColor3();
        }
        lineInfo.instance = linesMesh;
        lineInfo.instance.isPickable = false;

        return linesMesh;
    }
}

function evalHandlebarString(label: string, values: any) {
    if (!label.match(/\{.*\}/)) {
        return label;
    }
    const array = label.matchAll(/\{([\d\w+\-*/]+)\}/g);
    let exp = array.next();
    while (!exp.done) {
        let mathExp = exp.value[1];
        for (const key in values) {
            mathExp = mathExp.replace(key, values[key]);
        }
        if (mathExp.match(/\d/)) {
            // Strip all invalid math expression characters
            mathExp = mathExp.replace(/[^0-9.+*/-]/g, '');
            const numberVal = mathEval(mathExp);
            label = label.replace(exp.value[0], roundToPlaces(numberVal, 0.01).toString(10));
        } else {
            label = label.replace(exp.value[0], mathExp);
        }
        exp = array.next();
    }

    return label;
}

function getMultiLineStats(label: string, ctx: CanvasRenderingContext2D) {
    // 402 is a florin and 96 is a backtick, generally the tallest and highest characters.
    const fullStringTextSize = ctx.measureText(label + String.fromCharCode(402, 71, 95, 96, 103));

    const substrings: string[] = label.split('\\n').map(label => label.trim());
    const lineWidths: number[] = [];
    let maximumWidth = 0;
    let maximumHeight = 0;
    let maximumDescent = 0;
    substrings.forEach(string => {
        if (string.length > maximumWidth) {
            maximumWidth = string.length;
        }
        const textSize = ctx.measureText(string);

        if (textSize.actualBoundingBoxLeft !== undefined && textSize.actualBoundingBoxRight !== undefined) {
            const textWidth = Math.abs(textSize.actualBoundingBoxLeft) + Math.abs(textSize.actualBoundingBoxRight);
            if (textWidth > maximumWidth) {
                maximumWidth = textWidth;
            }
            lineWidths.push(textWidth);
        } else {
            if (textSize.width > maximumWidth) {
                maximumWidth = textSize.width;
            }
            lineWidths.push(textSize.width);
        }
    });

    // The render puppetteer browser does not have fontBoundingBoxAscent or Descent.
    // That would be a better way to measure text for consistency. When upgrading puppeteer see if we can use fontBoundingBoxAscent
    if (
        fullStringTextSize.actualBoundingBoxAscent !== undefined &&
        fullStringTextSize.actualBoundingBoxDescent !== undefined
    ) {
        maximumHeight =
            Math.abs(fullStringTextSize.actualBoundingBoxAscent) +
            Math.abs(fullStringTextSize.actualBoundingBoxDescent);
        maximumDescent = fullStringTextSize.actualBoundingBoxDescent;
    }

    if (maximumHeight === 0) {
        maximumHeight = TEXT_RESOLUTION;
    }

    const result = {
        labelArray: substrings,
        lineWidths,
        maximumHeight,
        maximumWidth,
        maximumDescent,
    };
    return result;
}

let loopCount = 0;
function mathEval(exp: string) {
    loopCount++;
    if (loopCount > 1000) {
        console.warn('escaped infinite loop in math evaluator');
        return NaN;
    }
    if (!exp.match(/\D/)) {
        return parseFloat(exp);
    }

    let result: number;
    if (exp.match(/\+/)) {
        const parts = exp.split('+').reverse();
        result = mathEval(parts.pop()!);
        while (parts.length > 0) {
            result += mathEval(parts.pop()!);
        }
    } else if (exp.match(/-/)) {
        const parts = exp.split('-').reverse();
        result = mathEval(parts.pop()!);
        while (parts.length > 0) {
            result -= mathEval(parts.pop()!);
        }
    } else if (exp.match(/\*/)) {
        const parts = exp.split('*').reverse();
        result = mathEval(parts.pop()!);
        while (parts.length > 0) {
            result *= mathEval(parts.pop()!);
        }
    } else if (exp.match(/\//)) {
        const parts = exp.split('/').reverse();
        result = mathEval(parts.pop()!);
        while (parts.length > 0) {
            result /= mathEval(parts.pop()!);
        }
    } else {
        result = parseFloat(exp);
    }

    return result;
}
